import React, { useEffect, useRef } from "react";

// Sets all Goal/Actual PPH values to "--" immediately so no stale hardcoded numbers
// are visible while the API call is in flight.
function clearPPHNumbers(doc) {
  const dts = doc.querySelectorAll("dt");
  dts.forEach((dt) => {
    const label = dt.textContent.trim().toLowerCase();
    if (label !== "goal" && label !== "actual") return;
    const dd = dt.nextElementSibling;
    if (!dd) return;
    for (const child of dd.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        child.nodeValue = "--";
        break;
      }
    }
  });
}

// Card color themes — values match what light-overrides.css produces for the static Tailwind classes,
// so dynamic cards look identical to the static ones in light theme.
// Rules: >= 95% of goal → green, < 95% → amber, bottleneck → red (applied separately).
const CARD_THEMES = {
  green: {
    borderColor: "rgba(52, 211, 153, 0.6)",
    background: "linear-gradient(to bottom right, rgba(16, 185, 129, 0.18), rgba(16, 185, 129, 0.12), #ecfdf5)",
  },
  amber: {
    borderColor: "rgba(251, 146, 60, 0.6)",
    background: "linear-gradient(to bottom right, rgba(251, 191, 36, 0.18), rgba(251, 191, 36, 0.10), #fffbeb)",
  },
  red: {
    borderColor: "rgba(220, 38, 38, 0.85)",
    background: "linear-gradient(to bottom right, rgba(220, 38, 38, 0.22), rgba(220, 38, 38, 0.12), #fff1f2)",
  },
};

// Binary threshold: >= 95% → green, anything less → amber.
// Red is reserved exclusively for the bottleneck card.
function getCardTheme(actual, goal) {
  if (!goal || goal === 0) return CARD_THEMES.amber;
  const pct = (actual / goal) * 100;
  return pct >= 95 ? CARD_THEMES.green : CARD_THEMES.amber;
}

// Identifies the bottleneck: process with the LOWEST actual PPH (constrains factory throughput).
// Tiebreaker: lowest process number (most upstream process).
function findBottleneck(rows) {
  if (!rows || rows.length === 0) return null;
  // Extract trailing process number for tiebreaking (e.g. "Process 3" → 3)
  const processOrder = (name) => {
    const m = name && name.match(/(\d+)$/);
    return m ? parseInt(m[1], 10) : 999;
  };
  const best = rows.reduce((winner, row) => {
    const pph = row.avg_daily_PPH || 0;
    const winnerPph = winner.avg_daily_PPH || 0;
    if (pph < winnerPph) return row;
    if (pph === winnerPph && processOrder(row.process) < processOrder(winner.process)) return row;
    return winner;
  });
  return best.process ? best.process.trim() : null;
}

// Applies red theme to the bottleneck card and floats a dialog box directly above it.
function markBottleneck(doc, bottleneckName, rows, onAgentDeepDive, dateLabel) {
  if (!bottleneckName) return;

  // Clean up any stale wrapper/alert from a previous inject
  const staleWrapper = doc.getElementById('bottleneck-wrapper');
  if (staleWrapper) {
    const parent = staleWrapper.parentElement;
    if (parent) parent.insertBefore(staleWrapper.querySelector('[id^="bn-card"]') || staleWrapper.firstChild, staleWrapper);
    staleWrapper.remove();
  }

  let bottleneckCard = null;

  // Apply red theme to the bottleneck card
  const spans = doc.querySelectorAll('[class*="rounded-2xl"] span');
  for (const span of spans) {
    const rawText = span.childNodes[0]?.nodeValue?.trim() || span.textContent.trim();
    if (rawText === bottleneckName) {
      bottleneckCard = span.closest('[class*="rounded-2xl"]');
      if (bottleneckCard) {
        bottleneckCard.style.borderColor = CARD_THEMES.red.borderColor;
        bottleneckCard.style.background = CARD_THEMES.red.background;
      }
      break;
    }
  }

  if (!bottleneckCard) return;

  // Gap info for the dialog
  const row = rows && rows.find(r => r.process && r.process.trim() === bottleneckName);
  const gap = row ? Math.round((row.planned_daily_PPH || 0) - (row.avg_daily_PPH || 0)) : null;
  const pct = (row && row.planned_daily_PPH)
    ? Math.round(((row.planned_daily_PPH - row.avg_daily_PPH) / row.planned_daily_PPH) * 100)
    : null;

  // Detect if bottleneck is QCS (Process 1) - on left side, needs right-side alert
  const isQCS = bottleneckName.includes('Process 1') || bottleneckName.toLowerCase().includes('qcs');

  // Wrapper: position:relative so the dialog can be absolute-positioned
  const wrapper = doc.createElement('div');
  wrapper.id = 'bottleneck-wrapper';
  wrapper.style.cssText = isQCS 
    ? 'position:relative;display:inline-flex;flex-direction:row;align-items:stretch;'
    : 'position:relative;display:inline-flex;flex-direction:column;align-items:stretch;';

  // Dialog box
  // Inject blink keyframes into the iframe document once
  if (!doc.getElementById('bn-blink-style')) {
    const style = doc.createElement('style');
    style.id = 'bn-blink-style';
    style.textContent = `
      @keyframes bn-blink {
        0%,100% { opacity:1; }
        50%      { opacity:0.15; }
      }
      .bn-blink-anim {
        animation: bn-blink 0.55s ease-in-out 3;
      }
    `;
    doc.head.appendChild(style);
  }

  const dialog = doc.createElement('div');
  dialog.id = 'bottleneck-alert';
  dialog.classList.add('bn-blink-anim');
  
  // Position on RIGHT side for QCS, TOP for others
  const dialogStyles = isQCS ? [
    'position:absolute',
    'left:calc(100% + 10px)',
    'top:50%',
    'transform:translateY(-50%)',
    'width:220px',
    'background:#fff7f7',
    'border:1px solid rgba(220,38,38,0.35)',
    'border-left:3px solid rgba(220,38,38,0.85)',
    'border-radius:8px',
    'padding:10px 14px 9px',
    'font-family:inherit',
    'font-size:0.72rem',
    'line-height:1.5',
    'color:#991b1b',
    'box-shadow:0 4px 14px rgba(220,38,38,0.13),0 1px 3px rgba(0,0,0,0.07)',
    'z-index:10',
    'white-space:normal',
    'word-break:break-word',
    'text-align:center',
  ] : [
    'position:absolute',
    'bottom:calc(100% + 10px)',
    'left:0',
    'right:0',
    'background:#fff7f7',
    'border:1px solid rgba(220,38,38,0.35)',
    'border-top:3px solid rgba(220,38,38,0.85)',
    'border-radius:8px',
    'padding:10px 14px 9px',
    'font-family:inherit',
    'font-size:0.72rem',
    'line-height:1.5',
    'color:#991b1b',
    'box-shadow:0 4px 14px rgba(220,38,38,0.13),0 1px 3px rgba(0,0,0,0.07)',
    'z-index:10',
    'white-space:normal',
    'word-break:break-word',
    'text-align:center',
  ];
  
  dialog.style.cssText = dialogStyles.join(';');

  // Title row
  const titleRow = doc.createElement('div');
  titleRow.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:6px;margin-bottom:3px;';
  const iconEl = doc.createElement('span');
  iconEl.textContent = '⚠';
  iconEl.style.cssText = 'font-size:0.85rem;';
  const titleEl = doc.createElement('span');
  titleEl.textContent = 'Bottleneck';
  titleEl.style.cssText = 'font-weight:700;font-size:0.73rem;letter-spacing:0.04em;text-transform:uppercase;color:rgba(185,28,28,1);';
  titleRow.appendChild(iconEl);
  titleRow.appendChild(titleEl);

  // Body — two lines
  const body = doc.createElement('div');
  body.style.cssText = 'color:#7f1d1d;font-size:0.71rem;line-height:1.55;';
  const line1 = doc.createElement('div');
  line1.textContent = (gap !== null && pct !== null)
    ? `${gap} PPH (${pct}%) below target,`
    : gap !== null ? `${gap} PPH below target,` : 'Below target,';
  const line2 = doc.createElement('div');
  if (dateLabel) {
    line2.innerHTML = `Lowest PPH on the floor as of<br>${dateLabel}`;
  } else {
    line2.textContent = 'Lowest PPH on the floor';
  }
  body.appendChild(line1);
  body.appendChild(line2);

  // Caret arrow: LEFT-pointing for QCS, DOWNWARD for others
  const arrow = doc.createElement('div');
  const arrowStyles = isQCS ? [
    'position:absolute',
    'left:-7px',
    'top:50%',
    'transform:translateY(-50%) rotate(45deg)',
    'width:12px',
    'height:12px',
    'background:#fff7f7',
    'border-left:1px solid rgba(220,38,38,0.30)',
    'border-bottom:1px solid rgba(220,38,38,0.30)',
  ] : [
    'position:absolute',
    'bottom:-7px',
    'left:50%',
    'transform:translateX(-50%) rotate(45deg)',
    'width:12px',
    'height:12px',
    'background:#fff7f7',
    'border-right:1px solid rgba(220,38,38,0.30)',
    'border-bottom:1px solid rgba(220,38,38,0.30)',
  ];
  arrow.style.cssText = arrowStyles.join(';');

  // "Agentic AI Deepdive" button
  if (onAgentDeepDive) {
    const btn = doc.createElement('button');
    btn.textContent = '🤖 Agentic AI Deepdive';
    btn.style.cssText = [
      'display:inline-flex',
      'align-items:center',
      'gap:5px',
      'margin-top:9px',
      'padding:5px 12px',
      'background:rgba(220,38,38,0.08)',
      'border:1px solid rgba(220,38,38,0.35)',
      'border-radius:6px',
      'color:rgba(185,28,28,1)',
      'font-size:0.68rem',
      'font-weight:600',
      'letter-spacing:0.03em',
      'cursor:pointer',
      'font-family:inherit',
      'transition:background 0.15s',
    ].join(';');
    btn.onmouseover = () => { btn.style.background = 'rgba(220,38,38,0.16)'; };
    btn.onmouseout  = () => { btn.style.background = 'rgba(220,38,38,0.08)'; };
    btn.onclick = () => onAgentDeepDive(
      "Perform bottleneck analysis for my factory's production and compare yesterday's performance with the last 30 days'."
    );
    dialog.appendChild(titleRow);
    dialog.appendChild(body);
    dialog.appendChild(btn);
    dialog.appendChild(arrow);
  } else {
    dialog.appendChild(titleRow);
    dialog.appendChild(body);
    dialog.appendChild(arrow);
  }

  // Replace the card in the DOM with wrapper → dialog + card
  bottleneckCard.parentElement.insertBefore(wrapper, bottleneckCard);
  wrapper.appendChild(dialog);
  wrapper.appendChild(bottleneckCard);
}

// Updates card border and background color based on actual vs goal PPH ratio.
function updateCardColors(doc, rows) {
  if (!rows || rows.length === 0) return;

  rows.forEach((row) => {
    if (!row.process) return;
    const processName = row.process.trim();
    const theme = getCardTheme(row.avg_daily_PPH, row.planned_daily_PPH);

    // Find the card whose top <span> matches this process name
    const spans = doc.querySelectorAll('[class*="rounded-2xl"] span');
    for (const span of spans) {
      if (span.textContent.trim() === processName) {
        const card = span.closest('[class*="rounded-2xl"]');
        if (card) {
          card.style.borderColor = theme.borderColor;
          card.style.background = theme.background;
        }
        break;
      }
    }
  });
}

// Injects live PPH actual/goal numbers from the API into the iframe's process cards.
// rows: array of { process, avg_daily_PPH, planned_daily_PPH } from /api/pph
function injectPPHNumbers(doc, rows) {
  if (!rows || rows.length === 0) return;

  // Build lookup: "Process 1" -> { actual, goal }
  const pphMap = {};
  rows.forEach((row) => {
    if (row.process) {
      pphMap[row.process.trim()] = {
        actual: Math.round(row.avg_daily_PPH),
        goal: Math.round(row.planned_daily_PPH),
      };
    }
  });

  // Walk every <dt> in the iframe; match "Goal" and "Actual" labels
  const dts = doc.querySelectorAll("dt");
  dts.forEach((dt) => {
    const label = dt.textContent.trim().toLowerCase();
    if (label !== "goal" && label !== "actual") return;

    // Find the nearest ancestor card that contains "Process N"
    const card = dt.closest('[class*="rounded-2xl"]');
    if (!card) return;

    // The process label is in a <span> at the top of the card
    const processSpan = card.querySelector("span");
    if (!processSpan) return;
    const processName = processSpan.textContent.trim(); // e.g. "Process 1"
    const pph = pphMap[processName];
    if (!pph) return;

    const newValue = label === "goal" ? pph.goal : pph.actual;

    // <dd> is the next sibling of <dt>; the number is a raw text node before the <span> " PPH"
    const dd = dt.nextElementSibling;
    if (!dd) return;
    for (const child of dd.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        child.nodeValue = String(newValue);
        break;
      }
    }
  });
}

// Adds Value stream legend aligned to the right side of the QCS box.
function injectValueStreamLegend(doc) {
  const processMapSection =
    doc.querySelector("section.overflow-x-auto") ||
    (() => {
      const qcsLabel = Array.from(doc.querySelectorAll('[class*="rounded-2xl"] span')).find((span) => {
        const text = (span.childNodes[0]?.nodeValue || span.textContent || "").trim().toLowerCase();
        return text === "process 1" || text.includes("qcs");
      });
      const qcsCard = qcsLabel?.closest('[class*="rounded-2xl"]');
      return qcsCard?.closest("section") || qcsCard?.parentElement || null;
    })();

  if (!processMapSection) return false;

  const existing = doc.getElementById("value-stream-legend");
  if (existing) {
    processMapSection.style.position = "relative";
    processMapSection.style.overflow = "visible";
    if (existing.parentElement !== processMapSection) {
      processMapSection.appendChild(existing);
    }
    return true;
  }

  const legend = doc.createElement("div");
  legend.id = "value-stream-legend";
  legend.style.cssText = [
    "position:absolute",
    "right:12px",
    "top:12px",
    "z-index:12",
    "display:grid",
    "gap:8px",
    "padding:10px 14px",
    "border:1px solid rgba(148,163,184,0.35)",
    "border-radius:12px",
    "background:rgba(255,255,255,0.94)",
    "color:#0f172a",
    "box-shadow:0 4px 10px rgba(2,6,23,0.08)",
    "font-family:inherit",
    "min-width:260px",
    "max-width:320px",
  ].join(";");

  const title = doc.createElement("div");
  title.textContent = "Value stream legend";
  title.style.cssText = "font-size:12px;font-weight:700;letter-spacing:0.03em;text-transform:uppercase;color:#334155;";
  legend.appendChild(title);

  const items = [
    {
      label: "≥ 95% of goal",
      swatchBg: "rgba(16, 185, 129, 0.18)",
      swatchBorder: "rgba(52, 211, 153, 0.6)",
    },
    {
      label: "< 95% of goal",
      swatchBg: "rgba(251, 191, 36, 0.18)",
      swatchBorder: "rgba(251, 146, 60, 0.6)",
    },
    {
      label: "Bottleneck Operation",
      swatchBg: "rgba(220, 38, 38, 0.22)",
      swatchBorder: "rgba(220, 38, 38, 0.85)",
    },
  ];

  items.forEach((item) => {
    const row = doc.createElement("div");
    row.style.cssText = "display:flex;align-items:center;gap:8px;font-size:12px;color:#0f172a;";

    const dot = doc.createElement("span");
    dot.style.cssText = [
      "width:12px",
      "height:12px",
      "border-radius:2px",
      `background:${item.swatchBg}`,
      `border:1px solid ${item.swatchBorder}`,
      "display:inline-block",
      "flex:0 0 auto",
    ].join(";");

    const text = doc.createElement("span");
    text.textContent = item.label;

    row.appendChild(dot);
    row.appendChild(text);
    legend.appendChild(row);
  });

  processMapSection.style.position = "relative";
  processMapSection.style.overflow = "visible";
  processMapSection.appendChild(legend);
  return true;
}

function FactoryFloorView({ onAgentDeepDive }) {
  const src = "/factoryfloor/index.html?v=4";
  const iframeRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      const iframe = iframeRef.current;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (!doc?.body) return;
      injectValueStreamLegend(doc);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleIframeLoad = async () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    const legacyLegend = doc.getElementById("factory-legend-card");
    if (legacyLegend) legacyLegend.remove();
    const legacyWrapper = doc.getElementById("factory-legend-wrapper");
    if (legacyWrapper) legacyWrapper.remove();
    const applyCtaStyles = () => {
      const cta = doc.querySelector('a[href="https://www.stratalignsolution.com/"]');
      if (!cta) return;
      cta.style.padding = "1.15rem 2.18rem";
      cta.style.minWidth = "205px";
      cta.style.boxSizing = "border-box";
      cta.style.display = "flex";
      cta.style.flexDirection = "column";
      cta.style.alignItems = "center";
      cta.style.justifyContent = "center";
      cta.style.textAlign = "center";
    };
    applyCtaStyles();
    if (!doc.getElementById("cta-style-guard")) {
      const guard = doc.createElement("meta");
      guard.id = "cta-style-guard";
      const obs = new MutationObserver(() => applyCtaStyles());
      obs.observe(doc.documentElement, { childList: true, subtree: true, attributes: true });
      doc.head.appendChild(guard);
    }
    if (!doc.getElementById("light-mode-overrides")) {
      const link = doc.createElement("link");
      link.id = "light-mode-overrides";
      link.rel = "stylesheet";
      link.href = "/factoryfloor/light-overrides.css";
      doc.head.appendChild(link);
    }

    const replaceDoubleQuestionMarks = () => {
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        if (node.nodeValue && node.nodeValue.includes("??")) {
          node.nodeValue = node.nodeValue.replace(/\s*\?\?\s*/g, " ").replace(/\s+/g, " ").trim();
        }
        node = walker.nextNode();
      }
    };
    const ensureLegendVisible = () => injectValueStreamLegend(doc);

    replaceDoubleQuestionMarks();
    ensureLegendVisible();

    let legendRetryCount = 0;
    const legendRetryTimer = setInterval(() => {
      legendRetryCount += 1;
      const ready = ensureLegendVisible();
      if (ready || legendRetryCount >= 20) {
        clearInterval(legendRetryTimer);
      }
    }, 250);

    const observer = new MutationObserver(() => {
      replaceDoubleQuestionMarks();
      ensureLegendVisible();
    });
    observer.observe(doc.body, { childList: true, subtree: true, characterData: true });

    // Fetch live PPH data and inject into process cards
    // Production builds (PPE + PROD): call blobaccessapi directly.
    // blobaccessapi returns Access-Control-Allow-Origin: * so any SWA origin works.
    // Azure SWA staticwebapp.config.json rewrites to external https:// URLs do NOT
    // work — must use absolute URL for production. See ISSUE-001.
    const pphUrl = process.env.NODE_ENV === "production"
      ? "https://blobaccessapi.azurewebsites.net/api/pph?start=2025-06-30&end=2025-06-30"
      : "/api/pph?start=2025-06-30&end=2025-06-30";
    try {
      const res = await fetch(pphUrl);
      if (res.ok) {
        const data = await res.json();
        // Clear stale hardcoded numbers only after the API has responded — by this
        // point the Next.js iframe has already hydrated, avoiding React error #418.
        clearPPHNumbers(doc);
        injectPPHNumbers(doc, data.rows);
        updateCardColors(doc, data.rows);
        const dateMatch = pphUrl.match(/end=(\d{4}-\d{2}-\d{2})/);
        const dateLabel = dateMatch
          ? new Date(dateMatch[1] + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).replace(',', '')
          : '';
        markBottleneck(doc, findBottleneck(data.rows), data.rows, onAgentDeepDive, dateLabel);
        ensureLegendVisible();
      }
    } catch (_) {
      // API unavailable — hardcoded values in the static iframe remain
    }
  };
  return (
    <div className="card" style={{ padding: 0, height: "calc(100vh - 140px)" }}>
      <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
        <h2 style={{ margin: 0 }}>Windshield v/s Rear View</h2>
      </div>
      <iframe
        title="Operations Dashboard"
        src={src}
        ref={iframeRef}
        onLoad={handleIframeLoad}
        style={{ width: "100%", height: "100%", border: 0, background: "#f5f7fb" }}
      />
    </div>
  );
}

export default FactoryFloorView;
